import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography
} from "@material-ui/core";

import api from "../../services/api";
import OpportunityModal from "../OpportunityModal";
import LeadIntelligencePanel from "../LeadIntelligencePanel";

const getRows = payload => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.opportunities)) {
    return payload.opportunities;
  }
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return null;
};

const newest = rows =>
  [...rows].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0);
    const dateB = new Date(b.updatedAt || b.createdAt || 0);
    return dateB.getTime() - dateA.getTime();
  });

export default function ConversationLeadCRM({
  open,
  onClose,
  ticket,
  contact,
  onCreateOpportunity,
  onOpenContactTools
}) {
  const ticketId = ticket?.id || null;
  const contactId = contact?.id || ticket?.contactId || null;

  const [retry, setRetry] = useState(0);

  const [result, setResult] = useState({
    loading: false,
    error: false,
    opportunity: null
  });

  useEffect(() => {
    if (!open) return undefined;

    let active = true;

    setResult({
      loading: true,
      error: false,
      opportunity: null
    });

    const load = async () => {
      try {
        const lookups = [];

        if (ticketId) {
          lookups.push({
            field: "ticketId",
            id: ticketId
          });
        }

        if (contactId) {
          lookups.push({
            field: "contactId",
            id: contactId
          });
        }

        let selected = null;

        for (const lookup of lookups) {
          const response = await api.get("/opportunities", {
            params: {
              [lookup.field]: lookup.id,
              page: 1,
              pageSize: 50
            }
          });

          const rows = getRows(response.data);

          if (!Array.isArray(rows)) {
            throw new Error(
              "Formato inesperado na consulta de oportunidades"
            );
          }

          const matched = rows.filter(item => {
            if (lookup.field === "ticketId") {
              return String(item.ticketId) ===
                String(ticketId);
            }

            return String(
              item.contactId || item.contact?.id
            ) === String(contactId);
          });

          if (matched.length) {
            selected = newest(matched)[0];
            break;
          }
        }

        if (active) {
          setResult({
            loading: false,
            error: false,
            opportunity: selected
          });
        }
      } catch (error) {
        console.error(
          "[Korax CRM] Erro ao consultar oportunidades",
          error
        );

        if (active) {
          setResult({
            loading: false,
            error: true,
            opportunity: null
          });
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [open, ticketId, contactId, retry]);

  if (!open) return null;

  if (!result.loading &&
      !result.error &&
      result.opportunity) {
    return (
      <OpportunityModal
        open={open}
        onClose={onClose}
        ticketId={ticketId}
        contact={contact}
        opportunity={result.opportunity}
      />
    );
  }

  const dialogPaper = {
    width: "min(1090px, calc(100vw - 20px))",
    maxWidth: 1090,
    maxHeight: "94vh",
    borderRadius: 18,
    background: "#F7F9FD"
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      scroll="paper"
      PaperProps={{ style: dialogPaper }}
    >
      <DialogContent
        style={{
          padding: 18,
          background: "#F7F9FD"
        }}
      >
        <div
          style={{
            background: "#FFFFFF",
            padding: 18,
            marginBottom: 14,
            border: "1px solid #E5EBF3",
            borderRadius: 14,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              minWidth: 0
            }}
          >
            <Avatar
              src={contact?.urlPicture}
              style={{
                background: "#010B36",
                width: 48,
                height: 48
              }}
            >
              {(contact?.name || "C").charAt(0)}
            </Avatar>

            <div style={{ minWidth: 0 }}>
              <Typography
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#010B36"
                }}
              >
                {contact?.name || "Contato"}
              </Typography>

              <Typography
                style={{
                  fontSize: 12,
                  color: "#64748B"
                }}
              >
                {contact?.number || "Telefone não informado"}
              </Typography>
            </div>
          </div>

          <Button
            onClick={onClose}
            style={{
              textTransform: "none",
              fontWeight: 700,
              color: "#475569"
            }}
          >
            Fechar
          </Button>
        </div>

        {result.loading && (
          <div
            style={{
              padding: 55,
              textAlign: "center",
              color: "#64748B"
            }}
          >
            <CircularProgress size={27} />
            <Typography style={{ marginTop: 15 }}>
              Carregando CRM do cliente...
            </Typography>
          </div>
        )}

        {result.error && (
          <div
            style={{
              padding: 28,
              borderRadius: 14,
              background: "#FFFFFF",
              border: "1px solid #E5EBF3"
            }}
          >
            <Typography
              style={{
                fontWeight: 800,
                color: "#010B36"
              }}
            >
              Não foi possível carregar o CRM.
            </Typography>

            <Typography
              style={{
                color: "#64748B",
                marginTop: 6,
                marginBottom: 15,
                fontSize: 13
              }}
            >
              A consulta falhou. Nenhum registro foi criado
              ou modificado.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setRetry(value => value + 1)}
              style={{ textTransform: "none" }}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {!result.loading && !result.error && (
          <>
            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5EBF3",
                borderRadius: 14,
                padding: 17,
                marginBottom: 14,
                display: "flex",
                gap: 12,
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap"
              }}
            >
              <div>
                <Typography
                  style={{
                    fontWeight: 800,
                    color: "#010B36",
                    fontSize: 14
                  }}
                >
                  Sem oportunidade vinculada
                </Typography>

                <Typography
                  style={{
                    color: "#64748B",
                    fontSize: 12,
                    marginTop: 4
                  }}
                >
                  Os dados do contato continuam disponíveis.
                  Crie uma oportunidade somente quando necessário.
                </Typography>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap"
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onCreateOpportunity}
                  style={{
                    textTransform: "none",
                    fontWeight: 800,
                    background: "#0057FF"
                  }}
                >
                  Criar oportunidade
                </Button>

                <Button
                  variant="outlined"
                  onClick={onOpenContactTools}
                  style={{
                    textTransform: "none",
                    fontWeight: 700
                  }}
                >
                  Outras informações
                </Button>
              </div>
            </div>

            <LeadIntelligencePanel
              opportunity={null}
              contact={contact}
              ticketId={ticketId}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
